import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  DataTable,
  Dialog,
  Field,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type { Regulation, RegulationVersion } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { regulatoryApi } from "../../lib/api";
import { GOVERNANCE_PERMISSIONS, GovPanel, useGovernedMutation } from "../../governance";
import { usePermissions } from "../../governance";
import { VersionStatusBadge } from "./regulatoryLabels";

interface Row extends Regulation {
  versions: RegulationVersion[];
}

// Regulation registry (APP-004 R3 P4).
//
// Everything listed here is the tenant's own copy of an obligation. Odingard
// ships no regulatory text and this screen never offers to supply any: it
// registers the record, and the sections are imported by the people who hold
// the source.
export function GovRegulations() {
  const [params, setParams] = useSearchParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const search = params.get("q") ?? "";
  const jurisdiction = params.get("jurisdiction") ?? "";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const regulations = useAsync<Row[]>(async () => {
    const rows = (await regulatoryApi.regulations(orgId)).data.regulations;
    return Promise.all(
      rows.map(async (regulation) => ({
        ...regulation,
        versions: (await regulatoryApi.versions(orgId, regulation.id)).data.versions,
      })),
    );
  }, [orgId]);

  const jurisdictions = [
    ...new Set((regulations.data ?? []).map((row) => row.jurisdiction).filter(Boolean)),
  ].sort();

  const matches = (row: Row) => {
    const term = search.trim().toLowerCase();
    const hit =
      !term ||
      [row.name, row.regulationKey, row.authority, row.jurisdiction].some((value) =>
        value.toLowerCase().includes(term),
      );
    return hit && (!jurisdiction || row.jurisdiction === jurisdiction);
  };

  const activeVersion = (row: Row) => row.versions.find((v) => v.status === "active") ?? null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Regulations</h1>
          <p>
            The obligations this organization tracks, each held as versions of the text you supply.
            Odingard authors none of it: registering a regulation records the reference and the
            provenance, and your team imports the sections.
          </p>
        </div>
        <div className="badge-row">
          <RegisterRegulation orgId={orgId} onDone={regulations.reload} />
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="filter-row">
            <Field label="Search">
              {(id) => (
                <TextInput
                  id={id}
                  value={search}
                  placeholder="Name, key or authority"
                  onChange={(e) => setParam("q", e.target.value)}
                />
              )}
            </Field>
            <Field label="Jurisdiction">
              {(id) => (
                <Select
                  id={id}
                  value={jurisdiction}
                  onChange={(e) => setParam("jurisdiction", e.target.value)}
                >
                  <option value="">Any jurisdiction</option>
                  {jurisdictions.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </CardBody>
      </Card>

      <GovPanel
        title="Registered regulations"
        state={regulations}
        loadingLabel="Loading regulations…"
        isEmpty={(rows) => rows.filter(matches).length === 0}
        emptyTitle="No regulations match"
        emptyDescription="Register the obligations you are held to, then import the version you work from."
      >
        {(rows) => (
          <DataTable
            caption="Registered regulations"
            columns={[
              {
                key: "name",
                header: "Regulation",
                render: (row) => (
                  <>
                    <Link to={`/studio/governance/regulations/${row.id}`}>{row.name}</Link>
                    <div className="muted-note">
                      <code>{row.regulationKey}</code>
                    </div>
                  </>
                ),
              },
              { key: "jurisdiction", header: "Jurisdiction", render: (row) => row.jurisdiction || "Not stated" },
              { key: "authority", header: "Authority", render: (row) => row.authority || "Not stated" },
              {
                key: "active",
                header: "Active version",
                render: (row) => {
                  const version = activeVersion(row);
                  if (!version) return <span className="muted-note">No active version</span>;
                  return (
                    <>
                      {version.versionLabel}
                      <div className="badge-row">
                        <VersionStatusBadge status={version.status} />
                      </div>
                      <div className="muted-note">
                        In force {formatDate(version.effectiveDate)} · {version.sectionCount} sections
                      </div>
                    </>
                  );
                },
              },
              {
                key: "history",
                header: "Versions",
                render: (row) => (
                  <>
                    {row.versions.length}
                    <div className="muted-note">
                      {row.versions.length === 0
                        ? "Nothing imported yet"
                        : row.versions.map((v) => v.versionLabel).join(", ")}
                    </div>
                  </>
                ),
              },
            ]}
            rows={rows.filter(matches)}
          />
        )}
      </GovPanel>
    </>
  );
}

function RegisterRegulation({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const { can } = usePermissions();
  const verdict = can(GOVERNANCE_PERMISSIONS.regulationsManage);
  const [open, setOpen] = useState(false);
  const [regulationKey, setKey] = useState("");
  const [name, setName] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [authority, setAuthority] = useState("");
  const [description, setDescription] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.regulationsManage);

  const submit = async () => {
    const ok = await mutation.run(() =>
      regulatoryApi.registerRegulation(orgId, {
        regulationKey: regulationKey.trim(),
        name: name.trim(),
        jurisdiction: jurisdiction.trim(),
        authority: authority.trim(),
        description: description.trim(),
      }),
    );
    if (!ok) return;
    setOpen(false);
    setKey("");
    setName("");
    setJurisdiction("");
    setAuthority("");
    setDescription("");
    onDone();
  };

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={!verdict.allowed}
        aria-describedby={verdict.allowed ? undefined : "register-regulation-reason"}
      >
        Register regulation
      </Button>
      {!verdict.allowed && (
        <span className="ods-gate__reason" id="register-regulation-reason">
          {verdict.reason}
        </span>
      )}
      <Dialog
        open={open}
        title="Register a regulation"
        description="Records an obligation your organization is held to. The text stays yours: nothing is supplied by Odingard and nothing is published outside this tenant."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={regulationKey.trim().length === 0 || name.trim().length === 0 || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Registering…" : "Register"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Key" help="Short identifier your team uses for this obligation. It must be unique.">
          {(id) => <TextInput id={id} value={regulationKey} onChange={(e) => setKey(e.target.value)} />}
        </Field>
        <Field label="Name">
          {(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Jurisdiction">
          {(id) => (
            <TextInput id={id} value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} />
          )}
        </Field>
        <Field label="Regulator or authority">
          {(id) => <TextInput id={id} value={authority} onChange={(e) => setAuthority(e.target.value)} />}
        </Field>
        <Field label="Description" help="Why you track it. Do not paste licensed text here.">
          {(id) => (
            <TextArea
              id={id}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
      </Dialog>
    </>
  );
}
